/**
 * api.js — Centralized API service layer
 *
 * All backend calls go through this file.
 * Components never import axios directly — they import from here.
 *
 * Benefits:
 *  - Single place to change the base URL (env var)
 *  - Consistent error normalisation
 *  - Easy to add auth headers, retry logic, or mock in tests
 */

import axios from 'axios';
import { auth } from '../firebase';

// ── Base client ──────────────────────────────────────────────────────────────
export const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const client = axios.create({
    baseURL: BASE_URL,
    timeout: 90_000,
    headers: { 'Content-Type': 'application/json' },
});

// Intercept all requests and attach Firebase auth token
client.interceptors.request.use(async (config) => {
    try {
        if (auth.currentUser) {
            const token = await auth.currentUser.getIdToken();
            config.headers.Authorization = `Bearer ${token}`;
        }
    } catch (err) {
        console.error('Failed to get Firebase token:', err);
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

let isRefreshing = false;

client.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If 401 and we haven't already retried this request
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            if (isRefreshing) {
                // Already refreshing — just fail this request
                return Promise.reject(error);
            }

            isRefreshing = true;
            try {
                if (auth.currentUser) {
                    // Force-refresh the token (ignores cache)
                    const newToken = await auth.currentUser.getIdToken(true);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    isRefreshing = false;
                    return client(originalRequest);  // Retry the original request
                }
            } catch (refreshError) {
                isRefreshing = false;
                // Token refresh failed — user needs to log in again
                console.error('Token refresh failed:', refreshError);
            }
            isRefreshing = false;
        }

        return Promise.reject(error);
    }
);

function createApiError(err) {
    const rawError = err.response?.data?.error || err.response?.data?.message || err.message || 'An unexpected error occurred.';
    const error = new Error(rawError);
    if (err.response?.status === 402 || err.response?.data?.is_quota_error) {
        error.isQuotaError = true;
    }
    if (err.response?.status === 403 && rawError.includes('disabled for your account')) {
        error.isFeatureDisabled = true;
    }
    return error;
}

// ── Analytics ────────────────────────────────────────────────────────────────
export const analyticsApi = {
    /** Fetch aggregated platform analytics */
    getAll: async () => {
        try {
            const { data } = await client.get('/analytics');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Lead CRM ────────────────────────────────────────────────────────────────
export const leadApi = {
    getAll: async (status = null) => {
        try {
            const params = status ? { status } : {};
            const { data } = await client.get('/api/leads', { params });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    update: async (leadId, updateData) => {
        try {
            const payload = typeof updateData === 'string' ? { status: updateData } : updateData;
            const { data } = await client.put(`/api/leads/${leadId}`, payload);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    delete: async (leadId) => {
        try {
            const { data } = await client.delete(`/api/leads/${leadId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    create: async (leadData) => {
        try {
            const { data } = await client.post('/api/leads', leadData);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    }
};

// ── Posting ──────────────────────────────────────────────────────────────────
export const postApi = {
    /**
     * Batch post to multiple platforms.
     * @param {FormData} formData  — multipart/form-data with `posts` JSON + optional media files
     */
    post: async (formData) => {
        try {
            const { data } = await client.post('/post', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120_000,
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Fetch historic posts */
    getAll: async (limit = 50, platform = null) => {
        try {
            const params = { limit };
            if (platform) params.platform = platform;
            const { data } = await client.get('/api/posts', { params });
            return data.data || [];
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Media / R2 ──────────────────────────────────────────────────────────────
export const mediaApi = {
    initiateUpload: async (filename, contentType) => {
        try {
            const { data } = await client.post('/api/media/initiate-upload', { filename, contentType });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    presignPart: async (r2Path, uploadId, partNumber) => {
        try {
            const { data } = await client.post('/api/media/presign-part', { r2Path, uploadId, partNumber });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    completeUpload: async (uploadId, r2Path, mediaId, parts) => {
        try {
            const { data } = await client.post('/api/media/complete-upload', { uploadId, r2Path, mediaId, parts });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Content Analysis ─────────────────────────────────────────────────────────
export const analyzeApi = {
    /**
     * Analyse a piece of text for sentiment, engagement, readability, etc.
     * @param {string} text
     */
    analyze: async (text) => {
        try {
            const { data } = await client.post('/analyze', { text });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /**
     * Get AI suggestions for content optimization.
     */
    optimizeContent: async (params) => {
        try {
            const { data } = await client.post('/optimize', params, {
                timeout: 120_000
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    }
};

// ── Gap Analysis ─────────────────────────────────────────────────────────────
export const gapsApi = {
    /**
     * Full structured gap analysis between your content and a competitor.
     * Both params accept either raw text OR a URL (http/https).
     *
     * @param {string} yourContent       — your text or URL
     * @param {string} competitorContent — competitor text or URL
     * @returns {Promise<GapAnalysisResult>}
     */
    analyze: async (yourContent, competitorContent) => {
        try {
            const { data } = await client.post('/gaps', {
                current_content: yourContent,
                top_content: competitorContent,
            });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Check which optional backend features are available */
    health: async () => {
        try {
            const { data } = await client.get('/gaps/health');
            return data;
        } catch (_err) {
            // Health check failure is non-fatal — degrade gracefully
            return { url_fetch: false, ner: false };
        }
    },
};

// ── Rankings ─────────────────────────────────────────────────────────────────
export const rankApi = {
    /** Rank all posted content by engagement score */
    getRanked: async () => {
        try {
            const { data } = await client.get('/rank');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

export const ideasApi = {
    /**
     * Generate structured content ideas for a topic.
     * @param {string} topic
     * @param {string|null} platform  — optional platform filter e.g. 'Instagram'
     * @param {number} count          — number of ideas (default 5)
     */
    generate: async (topic, platform = null, count = 5) => {
        try {
            const { data } = await client.post('/ideas', { topic, platform, count });
            return data;   // { ideas: [...], topic, platform }
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Credentials (encrypted via backend) ──────────────────────────────────────
export const credentialsApi = {
    /**
     * Get all platform credentials (values are masked — just shows if set or not).
     * The backend decrypts the Fernet ciphertext before responding.
     */
    getAll: async () => {
        try {
            const { data } = await client.get('/api/credentials');
            return data;  // { instagram: { access_token: '••••••••' }, ... }
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Save platform credentials (backend encrypts before writing to Firestore).
     * @param {string} platform  — e.g. 'instagram'
     * @param {Object} fields    — { field_name: 'value', ... }
     */
    save: async (platform, fields) => {
        try {
            const { data } = await client.put(`/api/credentials/${platform}`, fields);
            return data;  // { status: 'saved', platform }
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Delete all credentials for a platform.
     * @param {string} platform  — e.g. 'instagram'
     */
    delete: async (platform) => {
        try {
            const { data } = await client.delete(`/api/credentials/${platform}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Get the YouTube OAuth URL to redirect the user to.
     */
    getYouTubeAuthUrl: async () => {
        try {
            const { data } = await client.get('/api/youtube/auth');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Submit the YouTube OAuth callback code to the backend.
     */
    handleYouTubeCallback: async (code) => {
        try {
            const { data } = await client.post('/api/youtube/callback', { code });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Billing ───────────────────────────────────────────────────────────────────
export const billingApi = {
    getConfig: async () => {
        try {
            const { data } = await client.get('/api/billing/config');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    createCheckoutSession: async (planId = 'pro', currency = 'usd') => {
        try {
            const { data } = await client.post('/api/billing/create-checkout-session', { planId, currency });
            return data; // { url: 'https://checkout.stripe.com/...' }
        } catch (err) {
            throw createApiError(err);
        }
    },
    createPortalSession: async () => {
        try {
            const { data } = await client.post('/api/billing/create-portal-session');
            return data; // { url: 'https://billing.stripe.com/...' }
        } catch (err) {
            throw createApiError(err);
        }
    },
    getPublicToken: async () => {
        try {
            const { data } = await client.get('/api/billing/get-public-token');
            return data.token; // The signed JWT
        } catch (err) {
            throw createApiError(err);
        }
    },
    getPublicConfig: async (token) => {
        try {
            const { data } = await client.get('/api/billing/public-config', {
                headers: token ? {
                    'Authorization': `Bearer ${token}`
                } : {}
            });
            return data; // { plans: { ... } }
        } catch (err) {
            throw createApiError(err);
        }
    }
};

// ── Automation ──────────────────────────────────────────────────────────────
export const automationApi = {
    /** Get AI auto-responder settings */
    getSettings: async () => {
        try {
            const { data } = await client.get('/automation/settings');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Save AI auto-responder settings */
    updateSettings: async (settings) => {
        try {
            const { data } = await client.post('/automation/settings', settings);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Get pending replies requiring manual review */
    getPendingReplies: async () => {
        try {
            const { data } = await client.get('/engagement/pending');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Approve/Send pending reply, optionally edited */
    approveReply: async (docId, text = null) => {
        try {
            const { data } = await client.post(`/engagement/approve/${docId}`, { text });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Reject and delete pending reply */
    rejectReply: async (docId) => {
        try {
            const { data } = await client.post(`/engagement/reject/${docId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    }
};

// ── Newsletter ─────────────────────────────────────────────────────────────
export const newsletterApi = {
    /**
     * Subscribe a user to the newsletter/waitlist.
     * @param {string} email
     * @param {Object} metadata
     */
    subscribe: async (email, metadata = {}) => {
        try {
            const { data } = await client.post('/newsletter/subscribe', { email, metadata });
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /** Fetch active subscriber count for social proof */
    getCount: async () => {
        try {
            const { data } = await client.get('/newsletter/count');
            return data;
        } catch (err) {
            // Social proof is non-critical, but we'll still throw for the component to handle if it wants
            throw createApiError(err);
        }
    },
};

// ── Trending ─────────────────────────────────────────────────────────────
export const trendingApi = {
    /** Fetch real-time trending topics from Google Trends */
    getTrending: async (region = 'united_states', platform = 'web') => {
        try {
            const { data } = await client.get(`/trending?region=${region}&platform=${platform}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ─── Scheduled Posts (Calendar) ─────────────────────────────────────────────
export const scheduledPostsApi = {
    /**
     * Fetch scheduled posts for a date range.
     * @param {string} start - ISO 8601 date string (inclusive)
     * @param {string} end   - ISO 8601 date string (inclusive)
     * @param {string} [platform] - Optional platform filter
     */
    getRange: async (start, end, platform) => {
        try {
            const params = { start, end };
            if (platform) params.platform = platform;
            const { data } = await client.get('/api/calendar/posts', { params });
            return data.posts || [];
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Get a single scheduled post by its Firestore document ID.
     * @param {string} postId
     */
    getById: async (postId) => {
        try {
            const { data } = await client.get(`/api/calendar/posts/${postId}`);
            return data.post;
        } catch (err) {
            throw createApiError(err);
        }
    },

    /**
     * Cancel (delete) a scheduled post.
     * @param {string} postId
     */
    cancel: async (postId) => {
        try {
            const { data } = await client.delete(`/api/calendar/posts/${postId}`);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
};

// ── Features Toggles ───────────────────────────────────────────────────────
export const featuresApi = {
    /** Fetch evaluated feature toggles for the current user */
    getFeatures: async () => {
        try {
            const { data } = await client.get('/features');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Fetch raw feature toggles config (Admin only) */
    getAdminToggles: async () => {
        try {
            const { data } = await client.get('/admin/toggles');
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Update feature toggles config (Admin only) */
    updateAdminToggles: async (updateData) => {
        try {
            const { data } = await client.put('/admin/toggles', updateData);
            return data;
        } catch (err) {
            throw createApiError(err);
        }
    },
    /** Public — no auth required. Returns { waitlist_mode: true/false } */
    getWaitlistMode: async () => {
        try {
            const { data } = await client.get('/public/waitlist-mode');
            return data.waitlist_mode === true;
        } catch (err) {
            return false; // fail-open: if unreachable, show live site
        }
    },
};

// ── Default export (convenience) ─────────────────────────────────────────────
const api = {
    analyticsApi,
    postApi,
    mediaApi,
    analyzeApi,
    gapsApi,
    rankApi,
    ideasApi,
    credentialsApi,
    billingApi,
    automationApi,
    trendingApi,
    newsletterApi,
    scheduledPostsApi,
    featuresApi,
};

export default api;
